/**
 * Terakki Yönetimi (Admin) sayfalarının ortak yardımcıları: rol bazlı
 * yönlendirme, sayı / tarih biçimleri ve pano grafik verisinin hazırlanması.
 * Hepsi saf işlev; bileşenler ve testler aynı kaynağı kullanır.
 */

export const ADMIN_ROLE = "Admin";
export const ADMIN_HOME = "/sistem/pano";
export const USER_HOME = "/uygulama/profil";

export function isAdmin(user) {
  return user?.role === ADMIN_ROLE;
}

/** Girişten sonra ve yanlış bölümde gidilecek ana sayfa. */
export function homePathForRole(role) {
  return role === ADMIN_ROLE ? ADMIN_HOME : USER_HOME;
}

/** Hesap rolü yalnız Kullanıcı ya da Sistem; yöneticilik grup/hatim düzeyinde. */
export const ROLE_LABELS = {
  User: "Kullanıcı",
  Admin: "Sistem",
};

/** Şemada kalan eski Manager/HighManager değerleri Kullanıcı gösterilir. */
const LEGACY_USER_ROLES = ["Manager", "HighManager"];

export function roleLabel(role) {
  if (LEGACY_USER_ROLES.includes(role)) return ROLE_LABELS.User;
  return ROLE_LABELS[role] || role || "";
}

const MONTHS_SHORT = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

/** 12345 → "12.345"; null/undefined → "0". */
export function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("tr-TR");
}

/** "2026-09-30" → "30 Eyl" (saat dilimi kaymadan). */
export function shortDayLabel(ymd) {
  if (typeof ymd !== "string") return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  return `${Number(m[3])} ${MONTHS_SHORT[Number(m[2]) - 1] || ""}`.trim();
}

/** ISO tarih → "30 Eyl 2026"; boşsa "". */
export function formatDate(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y] = value.split("-");
    return `${shortDayLabel(value)} ${y}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

/** ISO tarih → "30 Eyl 2026 · 14:05". */
export function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${formatDate(d)} · ${hh}:${mm}`;
}

/** ISO tarih → "14:05". */
export function formatTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Kayıt ve günlük aktif serilerini aynı gün eksenine dizer. Sunucu iki
 * seriyi de son 30 gün için verir; biri eksikse o günü 0 sayarız.
 */
export function mergeDailySeries(signupsDaily, dailyActive) {
  const map = new Map();
  (signupsDaily || []).forEach((d) => {
    if (!d?.date) return;
    map.set(d.date, { date: d.date, signups: Number(d.count) || 0, active: 0 });
  });
  (dailyActive || []).forEach((d) => {
    if (!d?.date) return;
    const cur = map.get(d.date) || { date: d.date, signups: 0, active: 0 };
    cur.active = Number(d.count) || 0;
    map.set(d.date, cur);
  });
  return Array.from(map.values()).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** Boş (null / undefined / "") alanları istek gövdesinden atar. */
export function compactBody(body) {
  const out = {};
  Object.entries(body || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    out[k] = v;
  });
  return out;
}

/** Doğru cevabın okunur hâli (soru tipine göre). */
export function correctAnswerText(question) {
  if (!question) return "";
  if (question.type === "coktan") {
    const idx = question.correctIndex;
    const opt = Array.isArray(question.options) ? question.options[idx] : undefined;
    return opt !== undefined ? opt : "";
  }
  if (question.type === "evethayir") {
    if (question.correctBool === undefined || question.correctBool === null) return "";
    return question.correctBool ? "Evet" : "Hayır";
  }
  if (question.type === "dogruyanlis") {
    if (question.correctBool === undefined || question.correctBool === null) return "";
    return question.correctBool ? "Doğru" : "Yanlış";
  }
  return "";
}

export const QUESTION_TYPE_LABELS = {
  coktan: "Çoktan seçmeli",
  evethayir: "Evet / Hayır",
  dogruyanlis: "Doğru / Yanlış",
  acik: "Açık uçlu",
};
